import { ProductAttributeSelectorProps } from '../config.types';
import React from 'react';
import { ChoiceGroup, useCustomizationContext } from '@apps/card/routes/CardCustomizer';
import { useGetAttributeDataForSelector } from './hooks/getAttributeDataForSelector';
import { Option } from '../product.types';
import { setAttributeStateWithValidation } from '../utils/state/setAttributeStateWithValidation';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';

type GenericAttributeProps = {
  getOptionComponent: (option: Option, isPossible: boolean, index: number) => JSX.Element;
  getChoiceGroupName: (option: Option) => string;
};

export type ExtraStyleProps = {
  minHeight?: number;
  direction?: 'row' | 'column';
  columns?: number;
  hideValueFromTitle?: boolean;
};

export const GenericChoiceGroupAttribute = (props: ProductAttributeSelectorProps & GenericAttributeProps & ExtraStyleProps) => {
  const { product, attributeId, priceCalculator, getOptionComponent, getChoiceGroupName, getFullState, ...rest } = props;
  const data = useGetAttributeDataForSelector(props);
  const formatPrice = useMinorUnitsToPriceString();

  const [cardQuantity] = useCustomizationContext(draft => draft.config.customizations.quantity);

  if (!data || !data.options.selected) {
    return null;
  }

  const selectedOptionId = data.options.selected.optionId;
  const selectedOptionPrice = priceCalculator.getOptionPrice(product.id, { attributeId, optionId: selectedOptionId }, cardQuantity);
  const formattedOptionPrice = formatPrice(selectedOptionPrice, { canShowFreeLabel: true });

  const passIdToChoiceGroupNameProp = (id: string): string => {
    const option = data?.options.uiConfigurations.find(option => option?.optionId === id);
    return getChoiceGroupName(option!);
  };

  const validate = (optionId: string) => {
    const change = {
      attributeId,
      optionId: optionId,
    };
    const isValid = priceCalculator.doesSelectionCreateValidVariantGroup(product.id, getFullState(data.draftData), change);
    return isValid;
  };

  return (
    <ChoiceGroup
      getName={passIdToChoiceGroupNameProp}
      title={data.attribute.attributeUIConfig.title}
      value={selectedOptionId ?? ''}
      price={selectedOptionPrice === 0 ? formattedOptionPrice : `+ ${formattedOptionPrice}`}
      choices={data.options.uiConfigurations.map(opt => opt.optionId)}
      setValue={value => {
        setAttributeStateWithValidation(props, data, attributeId, value);
      }}
      subTitle={data.attribute.attributeUIConfig.subtitle}
      {...rest}
    >
      {data.options.uiConfigurations.map((option, index) => {
        return getOptionComponent(option, validate(option.optionId), index);
      })}
    </ChoiceGroup>
  );
};
